/*
  Elle Exist — bag behaviour
  ---------------------------------------------------------------------------
  Two things the theme's cart does not know about:

  1. The gift ladder, which counts pieces rather than money. It redraws on
     every cart render, reading the tiers off the element so a merchant can
     change them in the editor without touching this file.

  2. The add-on checkbox. Both directions go through the theme's own cart
     pipeline — ticking clicks the hidden product form, unticking clicks the
     remove link on the add-on's line item — so there is no second code path
     that could disagree with the cart the theme just drew.
*/
(function () {
  'use strict';

  var ADDED = 'theme:cart:added';

  function itemCount() {
    // the line items the theme has just rendered are the source of truth
    // the add-on is a service, not a piece — it must not climb the ladder
    var fields = document.querySelectorAll('[data-items-holder] [data-cart-item]:not([data-ee-care-line]) [data-quantity-field]');
    if (!fields.length) {
      return document.querySelectorAll('[data-items-holder] [data-cart-item]:not([data-ee-care-line])').length;
    }

    var total = 0;
    fields.forEach(function (field) {
      var value = parseInt(field.value, 10);
      if (!isNaN(value)) total += value;
    });
    return total;
  }

  function drawLadder(ladder, count) {
    var tiers = (ladder.dataset.eeLadderTiers || '')
      .split(',')
      .map(function (n) {
        return parseInt(n, 10) || 0;
      });
    var labels = (ladder.dataset.eeLadderLabels || '').split('|');
    if (!tiers.length) return;

    var top = Math.max.apply(null, tiers);
    var fill = ladder.querySelector('[data-ee-ladder-fill]');
    if (fill) fill.style.width = (top > 0 ? Math.min(100, Math.round((count / top) * 100)) : 0) + '%';

    ladder.querySelectorAll('[data-ee-ladder-step]').forEach(function (step) {
      var qty = parseInt(step.dataset.eeLadderQty, 10) || 0;
      step.classList.toggle('is-done', count >= qty);
    });

    // the first tier still out of reach is the one worth naming
    var next = -1;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i] > count) {
        next = i;
        break;
      }
    }

    var message = ladder.querySelector('[data-ee-ladder-message]');
    if (!message) return;

    if (next === -1) {
      message.textContent = ladder.dataset.eeLadderDone || '';
      return;
    }

    var short = tiers[next] - count;
    var template = short === 1 ? ladder.dataset.eeLadderOne : ladder.dataset.eeLadderMany;
    message.textContent = (template || '')
      .replace('[count]', short)
      .replace('[gift]', labels[next] || '');
  }

  function syncCare() {
    var line = document.querySelector('[data-ee-care-line]');
    document.querySelectorAll('[data-ee-care-toggle]').forEach(function (box) {
      box.checked = Boolean(line);
      box.disabled = false;
    });
  }

  /* The product rows are rendered once and never rebuilt — the theme unhides
     the empty state rather than re-rendering it — so a card that said "in bag"
     when the page loaded went on saying it after the bag was emptied. The line
     items are the truth; every button is restated from them. */
  function syncPairs() {
    var inCart = {};
    document.querySelectorAll('[data-items-holder] [data-ee-product]').forEach(function (line) {
      inCart[line.getAttribute('data-ee-product')] = true;
    });

    document.querySelectorAll('[data-ee-add]').forEach(function (button) {
      var added = Boolean(inCart[button.dataset.eeAdd]);
      button.classList.toggle('is-added', added);
      button.classList.remove('is-busy');
      button.disabled = added;

      var label = button.querySelector('.ee-cart-pair__btn-label');
      if (label) {
        label.textContent = added ? button.dataset.eeAddedLabel || '' : button.dataset.eeAddLabel || '';
      }
    });
  }

  /* Lines in the bag, add-on included — the gate for anything that only makes
     sense with something in it. Distinct from itemCount(), which counts
     pieces for the ladder and leaves the add-on out of the tally. */
  function lineCount() {
    return document.querySelectorAll('[data-items-holder] [data-cart-item]').length;
  }

  /* ---- which row changed ---------------------------------------------------
     The theme replaces the whole item list on every cart render, so the node
     that held the old number is gone by the time the new one arrives. The
     quantities are snapshotted before each render and compared after: the
     rows that actually moved get marked, and the CSS animates those alone. */
  var quantities = {};

  function readQuantities() {
    var map = {};
    document.querySelectorAll('[data-items-holder] [data-cart-item]').forEach(function (item) {
      var key = item.getAttribute('data-item');
      var field = item.querySelector('[data-quantity-field]');
      if (key && field) map[key] = parseInt(field.value, 10) || 0;
    });
    return map;
  }

  function markChanged() {
    var next = readQuantities();

    document.querySelectorAll('[data-items-holder] [data-cart-item]').forEach(function (item) {
      var key = item.getAttribute('data-item');
      var was = quantities[key];
      var now = next[key];
      if (was === undefined || was === now) return;

      // the number arrives from the direction it moved
      item.style.setProperty('--ee-qty-from', now > was ? '8px' : '-8px');
      item.classList.remove('ee-changed');
      void item.offsetWidth; // restart the animation on a repeat press
      item.classList.add('ee-changed');
    });

    quantities = next;
  }

  function refresh() {
    var count = itemCount();
    var lines = lineCount();

    markChanged();

    document.querySelectorAll('[data-ee-full-only]').forEach(function (node) {
      node.hidden = lines === 0;
    });

    document.querySelectorAll('[data-ee-ladder]').forEach(function (ladder) {
      drawLadder(ladder, count);
    });
    syncCare();
    syncPairs();
  }

  document.addEventListener('change', function (event) {
    var box = event.target.closest ? event.target.closest('[data-ee-care-toggle]') : null;
    if (!box) return;

    // the theme redraws the cart and flips the box back if the request fails
    box.disabled = true;

    if (box.checked) {
      var add = document.querySelector('[data-ee-care-add]');
      if (add) {
        add.click();
      } else {
        box.disabled = false;
      }
      return;
    }

    var remove = document.querySelector('[data-ee-care-line] [data-item-remove]');
    if (remove) {
      remove.click();
    } else {
      box.disabled = false;
    }
  });

  /* ---- discount code -------------------------------------------------------
     Shopify has no storefront call that applies a code to the cart, so the
     /discount/CODE route does it — the same link a campaign would use. Then
     the theme is asked to re-render the cart through its own pipeline, and
     the re-rendered markup is what says whether the code took. */

  var pending = null;

  function root() {
    return (window.theme && window.theme.routes && window.theme.routes.root) || '/';
  }

  function cartUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
  }

  function setMessage(text, ok) {
    document.querySelectorAll('[data-ee-discount-msg]').forEach(function (node) {
      node.textContent = text;
      node.hidden = !text;
      node.classList.toggle('is-ok', Boolean(ok));
      node.classList.toggle('is-bad', Boolean(text) && !ok);
    });
  }

  function busy(state) {
    document.querySelectorAll('[data-ee-discount]').forEach(function (box) {
      box.classList.toggle('is-busy', state);
    });
    document.querySelectorAll('[data-ee-discount-apply]').forEach(function (button) {
      button.disabled = state;
    });
  }

  /* A quantity update the theme performs itself: same quantity in, same
     quantity out, and a full re-render on the way back with every handler
     rebound. Cheaper than reimplementing build(). */
  function askThemeToRerender() {
    var item = document.querySelector('[data-items-holder] [data-cart-item]');
    if (!item) return false;

    var field = item.querySelector('[data-quantity-field]');
    var quantity = field ? parseInt(field.value, 10) : 1;
    if (!quantity || isNaN(quantity)) quantity = 1;

    item.dispatchEvent(
      new CustomEvent('theme:cart:update', {
        bubbles: true,
        detail: {id: item.getAttribute('data-item'), quantity: quantity},
      })
    );
    return true;
  }

  function appliedCodes() {
    var box = document.querySelector('[data-ee-discount]');
    if (!box) return [];
    return (box.dataset.eeDiscountApplied || '')
      .split('|')
      .map(function (code) {
        return code.trim().toLowerCase();
      })
      .filter(Boolean);
  }

  function verify() {
    if (!pending) return;

    var box = document.querySelector('[data-ee-discount]');
    var code = pending;
    pending = null;
    busy(false);

    var ok = appliedCodes().indexOf(code.toLowerCase()) !== -1;
    var template = box
      ? ok
        ? box.dataset.eeDiscountSuccess
        : box.dataset.eeDiscountError
      : '';

    setMessage((template || '').replace('[code]', code), ok);

    if (ok) {
      document.querySelectorAll('[data-ee-discount-input]').forEach(function (input) {
        input.value = '';
      });
    }
  }

  function apply(input) {
    var code = (input.value || '').trim();
    if (!code || pending) return;

    pending = code;
    busy(true);
    setMessage('', false);

    fetch(root() + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(cartUrl()), {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(function () {
        // verify() runs when the theme finishes redrawing the cart
        if (!askThemeToRerender()) verify();
      })
      .catch(function () {
        verify();
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-ee-discount-apply]') : null;
    if (!button) return;
    var box = button.closest('[data-ee-discount]');
    var input = box && box.querySelector('[data-ee-discount-input]');
    if (input) apply(input);
  });

  // the box sits inside the cart's own form: Enter must not send it to checkout
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var input = event.target.closest ? event.target.closest('[data-ee-discount-input]') : null;
    if (!input) return;
    event.preventDefault();
    apply(input);
  });

  /* ---- add from a product row ----------------------------------------------
     The visible button owns no form. It clicks the hidden one rendered for
     that product outside the cart's own form element, and the theme's
     add-to-cart takes it from there — same path as any product page. */
  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-ee-add]') : null;
    if (!button) return;

    var form = document.querySelector('[data-ee-add-form="' + button.dataset.eeAdd + '"]');
    if (!form) return;

    event.preventDefault();
    button.classList.add('is-busy');
    button.disabled = true;
    form.click();
  });

  document.addEventListener(ADDED, verify);
  document.addEventListener(ADDED, refresh);
  document.addEventListener('theme:cart:open', refresh);
  document.addEventListener('theme:cart:loaded', refresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
